import React, { useContext, useMemo } from "react";
import { RouteComponentProps } from "react-router";
import { useMediaQuery } from "@material-ui/core";
import {
  GenericDetailsCard,
  GenericDetailsCardComponent,
  GenericDetailsCardComponentEnum,
  GenericDetailsCardTopSectionComponent,
  GenericDetailsCardTopSectionComponentEnum,
  IconName,
  useDeviceTypes,
  getTotalPriceText,
  getProductByIndexFiat,
  roundToTwoDecimals,
  getIsExpirationDateEqualToCheckIn,
} from "halifax";
import {
  HotelCfarQuote,
  HotelCfarQuoteAdditionalInfo,
  HOTEL_CFAR_RESOLUTION,
  HOTEL_CFAR_TERMS_BODY,
  HOTEL_CFAR_TERMS_TABLE,
  RoomInfoProductsWithTransformedIndexes,
  FiatPrice,
} from "redmond";
import dayjs from "dayjs";
import clsx from "clsx";
import { CDN_ASSETS, getPortalName } from "@capone/common";
import { ClientContext } from "../../../../../../App";
import { PATH_TERMS_HOTEL_CFAR } from "../../../../../../utils/paths";
import { goToCheckout } from "../../../../../shop/utils/queryStringHelpers";
import type { RefundableRoomsCfarDetailsConnectorProps } from "./container";
import {
  PARTIAL_SCROLL_THRESHOLD,
  LOCATION_TEXT,
  REFUNDABLE_ROOM_TITLE_SEPARATE_LINES,
  REFUNDABLE_ROOM_TITLE,
  SELECT_AND_CONTINUE_BUTTON_COPY,
  STANDARDIZED_REFUNDABLE_ROOM_SUBTITLE,
  COVERAGE_UNDER_100_CFAR_DETAILS,
  CAP1_HOTEL_CFAR_REFUNDABLE_UPON_CANCELLATION,
  CAP1_HOTEL_CFAR_NONREFUNDABLE_UPON_CANCELLATION,
  WARNING_BANNER_GENERIC_COPY,
  READ_TERMS_AND_CONDITIONS_COPY,
  VIEW_TERMS_AND_CONDITIONS_COPY,
  STANDARDIZED_FULLY_REFUNDABLE_CFAR_DETAILS,
  STANDARDIZED_PARTIALLY_REFUNDABLE_CFAR_DETAILS,
} from "./constants";

import "./styles.scss";

export interface RefundableRoomsCfarDetailsProps
  extends RefundableRoomsCfarDetailsConnectorProps,
    RouteComponentProps {
  openCfarDetails: boolean;
  onClose?: () => void;
  // Note: Overwrite the default continue action
  onContinue?: () => void;
  contentClassName?: string;
  currentHotelCfarQuote: HotelCfarQuote;
  currentAdditionalInfo: HotelCfarQuoteAdditionalInfo | null;
  roomInfoProduct?: RoomInfoProductsWithTransformedIndexes;
  roomProductIndex?: number;
}

export const RefundableRoomsCfarDetails = (
  props: RefundableRoomsCfarDetailsProps
) => {
  const {
    history,
    openCfarDetails,
    onClose,
    onContinue = () => {
      goToCheckout({ history });
    },
    currentHotelCfarQuote,
    currentAdditionalInfo,
    selectedAccountReferenceId,
    contentClassName,
    hotelShopDateRange,
    roomInfoProduct,
    roomProductIndex,
    selectedLodging,
    nightCount,
  } = props;

  const { isAgentPortal } = useContext(ClientContext);
  const { matchesMobile } = useDeviceTypes();
  const usePartialScroll = useMediaQuery(PARTIAL_SCROLL_THRESHOLD, {
    noSsr: true,
  });

  const includeTaxesAndFees =
    selectedLodging?.price?.displayTaxesAndFeesIncluded;

  const {
    premiumPrices,
    premiumPricesPerNight,
    coveragePercentage,
    coverageAmount: quoteCoverageAmount,
    expirationDate,
  } = currentHotelCfarQuote;

  const isCoverageUnder100Percent = coveragePercentage < 100;
  const isCfarFeeRefundable = !!currentAdditionalInfo?.shouldRefundCfarPremium;
  const isTaxesIncludedInRefundPrice =
    !!currentAdditionalInfo?.totalRefundIncludesTaxes;

  const checkInDate = hotelShopDateRange?.from;
  const checkInTime = selectedLodging?.lodging.checkInInstructions?.checkInTime;

  const isExpirationDateEqualToCheckIn = getIsExpirationDateEqualToCheckIn(
    checkInDate,
    checkInTime,
    expirationDate
  );

  const quoteCoverageAmountFiat = {
    ...premiumPrices.fiat,
    value: quoteCoverageAmount.amount,
  };

  let roomProductDifferenceFiat: FiatPrice;
  if (roomInfoProduct && roomProductIndex) {
    const currentRoomProductFiat = getProductByIndexFiat(
      roomInfoProduct,
      roomProductIndex,
      includeTaxesAndFees,
      nightCount
    );
    const baseRoomProductFiat = getProductByIndexFiat(
      roomInfoProduct,
      0,
      includeTaxesAndFees,
      nightCount
    );

    roomProductDifferenceFiat = {
      ...currentRoomProductFiat,
      value: Math.ceil(
        roundToTwoDecimals(
          currentRoomProductFiat.value - baseRoomProductFiat.value
        )
      ),
    };
  }

  const topContent: GenericDetailsCardTopSectionComponent = {
    className: "hotel-cfar-details-header-with-image",
    image: matchesMobile
      ? CDN_ASSETS.capone.cfar.backgroundMobile
      : CDN_ASSETS.capone.cfar.backgroundDesktop,
    location: LOCATION_TEXT,
    header: matchesMobile
      ? REFUNDABLE_ROOM_TITLE_SEPARATE_LINES
      : REFUNDABLE_ROOM_TITLE,
    component:
      GenericDetailsCardTopSectionComponentEnum.HeaderWithImageBackground,
  };

  const handleViewTerms = () => {
    window.open(PATH_TERMS_HOTEL_CFAR, "_blank")?.focus();
  };

  const mainContent = useMemo(() => {
    const mainContentItems: GenericDetailsCardComponent[] = [];

    // Start continue button
    const continueCta: GenericDetailsCardComponent = {
      message: SELECT_AND_CONTINUE_BUTTON_COPY,
      onClick: onContinue,
      ariaLabel: SELECT_AND_CONTINUE_BUTTON_COPY,
      disabled: false,
      floating: matchesMobile,
      hidden: false,
      fill: "blue",
      component: GenericDetailsCardComponentEnum.GenericCta,
    };
    // End continue button

    // Start subtitle
    mainContentItems.push({
      className: clsx(
        "hotel-cfar-details-generic-copy",
        "refundable-room-subtitle",
        "bold"
      ),
      type: "secondary",
      copy: STANDARDIZED_REFUNDABLE_ROOM_SUBTITLE,
      component: GenericDetailsCardComponentEnum.GenericCopy,
    });
    // End subtitle

    // Start first bullet point
    const longExpirationDateString = expirationDate
      ? dayjs(expirationDate).format("MMMM D, YYYY [at] h:mm A")
      : "";

    const standardRefundString = currentAdditionalInfo?.baseRefundPrices?.fiat
      ? getTotalPriceText({
          price: currentAdditionalInfo.baseRefundPrices.fiat,
        })
      : "";

    const quoteCoverageAmountString = getTotalPriceText({
      price: quoteCoverageAmountFiat,
    });

    const firstBulletPointIcon = IconName.MoneyCounterClockwiseBlue;

    if (isCoverageUnder100Percent) {
      mainContentItems.push({
        className: "hotel-cfar-details-check-mark-icon",
        type: "secondary",
        copy: COVERAGE_UNDER_100_CFAR_DETAILS({
          expirationDateString: longExpirationDateString,
          expirationEqualToCheckIn: isExpirationDateEqualToCheckIn,
          coveragePercentage,
          coverageAmount: quoteCoverageAmountString,
        }),
        icon: firstBulletPointIcon,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    } else {
      const firstBulletPointCopy = isCfarFeeRefundable
        ? STANDARDIZED_FULLY_REFUNDABLE_CFAR_DETAILS({
            expirationDateString: longExpirationDateString,
            expirationEqualToCheckIn: isExpirationDateEqualToCheckIn,
            coverageAmount: standardRefundString,
            priceIncludesTaxes: isTaxesIncludedInRefundPrice,
          })
        : STANDARDIZED_PARTIALLY_REFUNDABLE_CFAR_DETAILS({
            expirationDateString: longExpirationDateString,
            expirationEqualToCheckIn: isExpirationDateEqualToCheckIn,
            coverageAmount: standardRefundString,
            priceIncludesTaxes: isTaxesIncludedInRefundPrice,
          });

      mainContentItems.push({
        className: "hotel-cfar-details-check-mark-icon",
        type: "secondary",
        copy: firstBulletPointCopy,
        icon: firstBulletPointIcon,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    }
    // End first bullet point

    // Start second bullet point
    const secondBulletPointCopy = HOTEL_CFAR_RESOLUTION(getPortalName());
    const secondBulletPointIcon = IconName.SimpleLaptopBlue;

    mainContentItems.push({
      className: clsx("hotel-cfar-details-check-mark-icon", "with-money-icon"),
      type: "secondary",
      copy: secondBulletPointCopy,
      icon: secondBulletPointIcon,
      component: GenericDetailsCardComponentEnum.GenericCopy,
    });
    // End second bullet point

    // Start third bullet point
    if (!isCoverageUnder100Percent) {
      const thirdBulletPointCopy = isCfarFeeRefundable
        ? CAP1_HOTEL_CFAR_REFUNDABLE_UPON_CANCELLATION(
            roomProductDifferenceFiat ??
              premiumPricesPerNight?.fiat ??
              premiumPrices.fiat,
            true
          )
        : CAP1_HOTEL_CFAR_NONREFUNDABLE_UPON_CANCELLATION(
            premiumPricesPerNight?.fiat ?? premiumPrices.fiat,
            true
          );
      const thirdBulletPointIcon = isCfarFeeRefundable
        ? IconName.CheckCircleBorder
        : IconName.NotAllowedSignBlue;

      mainContentItems.push({
        className: "hotel-cfar-details-check-mark-icon",
        type: "secondary",
        copy: thirdBulletPointCopy,
        icon: thirdBulletPointIcon,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    }
    // End third bullet point

    mainContentItems.push({
      copy: WARNING_BANNER_GENERIC_COPY,
      fill: "orange",
      size: "small",
      hideLeftIcon: true,
      hideRightIcon: true,
      useLeftBorder: true,
      component: GenericDetailsCardComponentEnum.NotificationBanner,
    });

    if (!usePartialScroll || matchesMobile) {
      mainContentItems.push(continueCta);
    }

    if (matchesMobile) {
      mainContentItems.push({
        content: [
          {
            title: READ_TERMS_AND_CONDITIONS_COPY,
            body:
              HOTEL_CFAR_TERMS_BODY(getPortalName()) + HOTEL_CFAR_TERMS_TABLE,
          },
        ],
        component: GenericDetailsCardComponentEnum.AccordionGroup,
      });
    } else if (!usePartialScroll) {
      mainContentItems.push({
        message: READ_TERMS_AND_CONDITIONS_COPY,
        onClick: handleViewTerms,
        position: "center",
        disabled: false,
        component: GenericDetailsCardComponentEnum.ClickableLink,
      });
    }

    return mainContentItems;
  }, [
    currentHotelCfarQuote,
    selectedAccountReferenceId,
    isAgentPortal,
    onContinue,
    matchesMobile,
    usePartialScroll,
  ]);

  const bottomContent: GenericDetailsCardComponent[] | undefined =
    useMemo(() => {
      if (!usePartialScroll || matchesMobile) {
        return undefined;
      }

      return [
        {
          message: VIEW_TERMS_AND_CONDITIONS_COPY,
          onClick: handleViewTerms,
          component: GenericDetailsCardComponentEnum.ClickableLink,
        },
        {
          className: clsx(
            "hotel-cfar-details-bottom-banner-continue-button",
            "refundable-room"
          ),
          message: SELECT_AND_CONTINUE_BUTTON_COPY,
          onClick: onContinue,
          ariaLabel: SELECT_AND_CONTINUE_BUTTON_COPY,
          disabled: false,

          floating: false,
          fill: "blue",
          component: GenericDetailsCardComponentEnum.GenericCta,
        },
      ];
    }, [onContinue, matchesMobile, usePartialScroll]);

  return (
    <GenericDetailsCard
      popupClassName="hotel-refundable-room-generic-details-card"
      contentClassName={contentClassName}
      topContent={topContent}
      mainContent={mainContent}
      bottomContent={bottomContent}
      openModal={openCfarDetails}
      onClose={onClose}
      isMobile={matchesMobile}
      contentOnly={false}
      scrollOption={usePartialScroll ? "partial-scroll" : "default-scroll"}
    />
  );
};
